import { getPricingProducts, yearlyMonths } from "../webapp/js/pricing-config.js";
import { calculatePrice } from "./ppp.js";

export const MIN_CHARGE_CENTS = 50;
/** Cloud SaaS with no download. Personal-use vs business-use only changes US sales tax. */
export const SAAS_CONSUMER_TAX_CODE = "txcd_10103000";

const EU_VAT_COUNTRIES = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR",
  "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK",
  "SI", "ES", "SE",
]);

export const CURRENT_MEMBERSHIP_STATUSES = ["active", "trialing", "past_due"];

/** Pay-as-you-go is billed only while Stripe can collect. */
export const OVERAGE_COLLECTABLE_STATUSES = ["active", "trialing"];

export function getBasicProduct() {
  return getPricingProducts().basic;
}

export function pricingProduct(productId) {
  return getPricingProducts()[productId] || null;
}

export function stripeProductIdForPlan(secretKey, productId = "basic") {
  const product = pricingProduct(productId) || getBasicProduct();
  const ids = product.stripeProductIds || {};
  const testMode = String(secretKey || "").startsWith("sk_test_");
  const id = testMode ? ids.test : ids.live;
  if (!id) {
    throw new Error("Stripe product id is not configured");
  }
  return id;
}

export function basicStripeProductId(secretKey) {
  return stripeProductIdForPlan(secretKey, "basic");
}

/** Pricing page: Germany shows VAT included, other EU countries show both. */
export function euPriceDisplay(alpha2) {
  const country = String(alpha2 || "").toUpperCase();
  if (!EU_VAT_COUNTRIES.has(country)) return "exclusive";
  if (country === "DE") return "inclusive";
  return "both";
}

export function creditAllowanceCents(feeCents) {
  const share = Number(getBasicProduct().creditShare) || 0;
  return Math.round(Number(feeCents || 0) * share);
}

/**
 * Tax-exclusive fee in euro cents for a PPP multiplier.
 * Yearly is yearlyMonths times the monthly amount.
 */
export function feeCentsForMultiplier(billingPeriod, pppMultiplier, product = getBasicProduct()) {
  const monthlyEuros = calculatePrice(
    product.pricing.monthly.base,
    pppMultiplier,
  );
  const monthlyCents = Math.round(monthlyEuros * 100);
  const months = billingPeriod === "yearly" ? yearlyMonths : 1;
  return monthlyCents * months;
}

export function feeCentsForCountry(billingPeriod, pppRows, countryCode, productId = "basic") {
  const product = pricingProduct(productId) || getBasicProduct();
  const country = (pppRows || []).find((row) => row.code === countryCode);
  const multiplier = country?.multiplier ?? 1;
  return feeCentsForMultiplier(billingPeriod, multiplier, product);
}

/**
 * EU invoices must not be paid while Stripe Tax is missing a registration.
 * Reverse charge and other zero-rate results are valid. Countries outside
 * the EU may stay uncollected until a registration exists.
 */
export function euVatCollectionError(invoice) {
  const country = invoice?.customer_address?.country;
  if (!country || !EU_VAT_COUNTRIES.has(country)) return null;
  const automatic = invoice.automatic_tax;
  if (automatic?.enabled && automatic.status && automatic.status !== "complete") {
    return `Stripe Tax is ${automatic.status} for ${country}`;
  }
  const taxes = invoice.total_taxes || invoice.total_tax_amounts || [];
  if (taxes.some((tax) => tax?.taxability_reason === "not_collecting")) {
    return `Stripe Tax is not collecting in ${country}`;
  }
  if (!taxes.length) {
    return `Stripe Tax did not return a result for ${country}`;
  }
  return null;
}

export function recurringInterval(billingPeriod) {
  return billingPeriod === "yearly" ? "year" : "month";
}

export function membershipPriceData({ productId, unitAmount, billingPeriod }) {
  return {
    currency: "eur",
    unit_amount: unitAmount,
    tax_behavior: "exclusive",
    product: productId,
    recurring: {
      interval: recurringInterval(billingPeriod),
    },
  };
}

export function oneTimePriceData({ productId, unitAmount }) {
  return {
    currency: "eur",
    unit_amount: unitAmount,
    tax_behavior: "exclusive",
    product: productId,
  };
}

/**
 * How many subscription-start anniversaries have passed.
 * The start day itself is month 0.
 */
export function billingMonthsElapsed(startUnix, nowUnix) {
  const start = Number(startUnix);
  const now = Number(nowUnix);
  if (!Number.isFinite(start) || !Number.isFinite(now) || now < start) {
    return 0;
  }
  const startDate = new Date(start * 1000);
  const nowDate = new Date(now * 1000);
  let months =
    (nowDate.getUTCFullYear() - startDate.getUTCFullYear()) * 12 +
    (nowDate.getUTCMonth() - startDate.getUTCMonth());
  if (nowDate.getUTCDate() < startDate.getUTCDate()) {
    months -= 1;
  }
  return Math.max(0, months);
}

export function overageInvoiceIsDue(subscription, nowUnix) {
  if (!subscription || subscription.pending_overage_invoice_id) {
    return false;
  }
  if (subscription.status === "canceled") return true;
  const elapsed = billingMonthsElapsed(
    subscription.current_period_start,
    nowUnix,
  );
  if (elapsed < 1) return false;
  const attempted = subscription.last_overage_attempt_at
    ? billingMonthsElapsed(
        subscription.current_period_start,
        subscription.last_overage_attempt_at,
      )
    : 0;
  return elapsed > attempted;
}
